import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { Trash, Add, Minus, ShoppingBag, Arrow, Back, Package } from '../../assets/icons';
import prod from '../../assets/images/prod_img.png';
import '../../assets/css/pages/cart.css';

const CartPage = () => {
    const navigate = useNavigate();
    const { cartItems, removeFromCart, updateQuantity, calculateTotal, clearCart } = useCart();
    const { isAuthenticated } = useAuth();

    const subtotal = useMemo(() => calculateTotal(), [calculateTotal]);
    
    // Group items by vendor
    const itemsByVendor = useMemo(() => {
        const grouped = {};
        cartItems.forEach(item => {
            const vendorName = item.vendor?.store_name || item.vendor_name || 'E-AquaHealth Store';
            const vendorId = item.vendor?.id || item.vendor_id || 'default';
            if (!grouped[vendorId]) {
                grouped[vendorId] = {
                    name: vendorName,
                    items: [],
                    subtotal: 0
                };
            }
            grouped[vendorId].items.push(item);
            grouped[vendorId].subtotal += (item.price * item.quantity);
        });
        return grouped;
    }, [cartItems]);

    const formatPrice = (price) => {
        return `₦${parseFloat(price || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
    };

    const handleProceedToCheckout = () => {
        if (!isAuthenticated) {
            // Store return URL
            localStorage.setItem('redirectAfterLogin', '/checkout');
            navigate('/login');
            return;
        }
        navigate('/checkout');
    };

    if (cartItems.length === 0) {
        return (
            <div className="cart-page">
                <div className="cart-empty">
                    <img src={ShoppingBag} alt="" width={80} height={80} />
                    <h2>Your Cart is Empty</h2>
                    <p>Looks like you haven&apos;t added any items to your cart yet.</p>
                    <Link to="/store" className="btn-continue-shopping">
                        <img src={Back} alt="" width={18} height={18} />
                        Continue Shopping
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="cart-page">
            <div className="cart-container">
                <div className="cart-header">
                    <h1>Shopping Cart</h1>
                    <span className="cart-count">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</span>
                </div>

                <div className="cart-layout">
                    <div className="cart-items-section">
                        {Object.entries(itemsByVendor).map(([vendorId, vendorData]) => (
                            <div key={vendorId} className="vendor-group">
                                <div className="vendor-header">
                                    <img src={Package} alt="" width={18} height={18} />
                                    <span>Sold by: <strong>{vendorData.name}</strong></span>
                                </div>
                                
                                <div className="cart-items">
                                    {vendorData.items.map((item) => (
                                        <div key={item.id} className="cart-item">
                                            <div className="item-image">
                                                <img 
                                                    src={item.image || item.images?.[0]?.image || prod} 
                                                    alt={item.name}
                                                    onError={(e) => { e.target.src = prod; }}
                                                />
                                            </div>
                                            
                                            <div className="item-details">
                                                <Link to={`/store/product/${item.id}`} className="item-name">
                                                    {item.name}
                                                </Link>
                                                {item.sku && <span className="item-sku">SKU: {item.sku}</span>}
                                                
                                                <div className="item-price-mobile">
                                                    {formatPrice(item.price)}
                                                </div>
                                                
                                                <div className="item-actions-mobile">
                                                    <div className="quantity-control">
                                                        <button 
                                                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                            disabled={item.quantity <= 1}
                                                            aria-label="Decrease quantity"
                                                        >
                                                            <img src={Minus} alt="" width={16} height={16} />
                                                        </button>
                                                        <span className="quantity">{item.quantity}</span>
                                                        <button 
                                                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                            disabled={item.quantity >= (item.stock_quantity || 99)}
                                                            aria-label="Increase quantity"
                                                        >
                                                            <img src={Add} alt="" width={16} height={16} />
                                                        </button>
                                                    </div>
                                                    
                                                    <button 
                                                        className="btn-remove"
                                                        onClick={() => removeFromCart(item.id)}
                                                        aria-label="Remove item"
                                                    >
                                                        <img src={Trash} alt="" width={18} height={18} />
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="item-price">
                                                {item.discount_price && item.discount_price < item.price ? (
                                                    <>
                                                        <span className="original-price">{formatPrice(item.price)}</span>
                                                        <span className="discount-price">{formatPrice(item.discount_price)}</span>
                                                    </>
                                                ) : (
                                                    <span>{formatPrice(item.price)}</span>
                                                )}
                                            </div>
                                            
                                            <div className="item-quantity">
                                                <div className="quantity-control">
                                                    <button 
                                                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                        disabled={item.quantity <= 1}
                                                        aria-label="Decrease quantity"
                                                    >
                                                        <img src={Minus} alt="" width={16} height={16} />
                                                    </button>
                                                    <input 
                                                        type="number"
                                                        value={item.quantity}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value);
                                                            if (val > 0 && val <= (item.stock_quantity || 99)) {
                                                                updateQuantity(item.id, val);
                                                            }
                                                        }}
                                                        min={1}
                                                        max={item.stock_quantity || 99}
                                                    />
                                                    <button 
                                                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                        disabled={item.quantity >= (item.stock_quantity || 99)}
                                                        aria-label="Increase quantity"
                                                    >
                                                        <img src={Add} alt="" width={16} height={16} />
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="item-total">
                                                {formatPrice(item.price * item.quantity)}
                                            </div>
                                            
                                            <button 
                                                className="btn-remove"
                                                onClick={() => removeFromCart(item.id)}
                                                aria-label="Remove item"
                                            >
                                                <img src={Trash} alt="" width={18} height={18} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="vendor-subtotal">
                                    Subtotal: {formatPrice(vendorData.subtotal)}
                                </div>
                            </div>
                        ))}
                        
                        <div className="cart-actions">
                            <Link to="/store" className="btn-continue">
                                <img src={Back} alt="" width={18} height={18} />
                                Continue Shopping
                            </Link>
                            <button className="btn-clear" onClick={() => clearCart()}>
                                <img src={Trash} alt="" width={18} height={18} />
                                Clear Cart
                            </button>
                        </div>
                    </div>

                    <div className="cart-summary">
                        <div className="summary-card">
                            <h2>Order Summary</h2>
                            
                            <div className="summary-row">
                                <span>Subtotal ({cartItems.reduce((acc, item) => acc + item.quantity, 0)} items)</span>
                                <span>{formatPrice(subtotal)}</span>
                            </div>
                            
                            <div className="summary-row shipping-note">
                                <span>Shipping</span>
                                <span>Calculated at checkout</span>
                            </div>
                            
                            <div className="summary-divider"></div>
                            
                            <div className="summary-row total">
                                <span>Estimated Total</span>
                                <span>{formatPrice(subtotal)}</span>
                            </div>
                            
                            <button 
                                className="btn-checkout"
                                onClick={handleProceedToCheckout}
                            >
                                Proceed to Checkout
                                <img src={Arrow} alt="" width={18} height={18} />
                            </button>
                            
                            <div className="secure-checkout">
                                <span>🔒 Secure Checkout</span>
                            </div>
                            
                            <div className="payment-methods">
                                <span>We Accept:</span>
                                <div className="payment-icons">
                                    <span className="payment-badge">Paystack</span>
                                    <span className="payment-badge">Cards</span>
                                    <span className="payment-badge">Bank Transfer</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="delivery-info-card">
                            <h3>Delivery Information</h3>
                            <ul>
                                <li>
                                    <strong>Standard Delivery:</strong> 2-5 business days
                                </li>
                                <li>
                                    <strong>Express Delivery:</strong> Same day or next day (select areas)
                                </li>
                                <li>
                                    Free shipping on orders over ₦50,000
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartPage;
